import { VM_STORAGE_KEYS } from '../../../adapter-interfaces/src';
import { getLocalStorageItem } from '../../../logger/src/LocalStorage';

/**
 * Webex Token enum key under which the signed-in user's org id is persisted
 * by `MsTeamsAuthenticationService`. Defined here as a string literal so this
 * utility stays independent of the `ms-teams-sdk` module (which is not part of
 * the widgets package).
 *
 * Keep this value in sync with `WebexTokenEnum.ORG_ID` in
 * `src/ms-teams-sdk/service/MsTeamsAuthenticationService.ts`.
 */
const WEBEX_ORG_ID_STORAGE_KEY = 'webex_orgId';

/**
 * Allow-list of Webex organization ids that have explicitly requested a
 * shorter voicemail polling interval (10 seconds instead of the default 60s).
 *
 * Org ids are case-insensitive UUIDs and are compared after trimming and
 * lower-casing the value read from localStorage to avoid any whitespace or
 * casing mismatches.
 *
 * Add new orgs here only after the customer has approved the additional
 * polling cost on the voicemail backend.
 */
export const FAST_VOICEMAIL_POLLING_ORG_IDS: ReadonlyArray<string> = [
  // UHS (Universal Health Services) — customer request to reduce voicemail
  // notification delay from 1 minute to ~10 seconds.
  'b1109f3e-8a85-4727-9d66-bcfa884de3e9',

  // Ford Motor Company (production) — customer request to reduce voicemail
  // notification delay from 1 minute to ~10 seconds.
  'af742d39-7515-46a3-82a7-03269e091b91',

  // Internal developer test orgs used to validate the fast-polling behaviour
  // before / alongside customer production rollouts. Safe to leave enabled in
  // these orgs since they are dev-owned and have no production traffic load
  // concerns.
  //
  // INT — Kamal & Vijay
  'bb428fef-9385-44ce-82f7-9891b8b1fdf6',
  // INT — Ganga & Yamuna
  '8845b4ec-5fde-49df-8837-53932da40be2',
  // PROD — Wick & Jack (developer test org on production hostname)
  'b6ee0aee-bcce-4324-a3c5-4243a7837c84',
];

const FAST_VOICEMAIL_POLLING_ORG_ID_SET: ReadonlySet<string> = new Set(
  FAST_VOICEMAIL_POLLING_ORG_IDS.map((id) => id.trim().toLowerCase()),
);

/**
 * Returns true when the supplied org id is in the fast-polling allow-list.
 *
 * @param orgId - Webex organization id (case-insensitive). May be `null`,
 *   `undefined`, or an empty string for unauthenticated states; those cases
 *   return `false`.
 */
export function isFastVoicemailPollingOrg(orgId: string | null | undefined): boolean {
  if (!orgId || typeof orgId !== 'string') {
    return false;
  }

  return FAST_VOICEMAIL_POLLING_ORG_ID_SET.has(orgId.trim().toLowerCase());
}

/**
 * Reads the signed-in user's org id from localStorage and returns the voicemail
 * polling interval (in milliseconds) that should be used for them.
 *
 * Falls back to the default 60-second interval when the org id is not present
 * (e.g. before sign-in) or when it is not in the fast-polling allow-list.
 */
export function getVoicemailPollingInterval(): number {
  const orgId = getLocalStorageItem<string>(WEBEX_ORG_ID_STORAGE_KEY);

  return isFastVoicemailPollingOrg(orgId)
    ? VM_STORAGE_KEYS.VM_TEN_SECONDS
    : VM_STORAGE_KEYS.VM_ONE_MINUTE;
}
